import { IssueCaseDoc, IssueLiabilityDoc, IssueTaskCategory, IssueTaskDoc } from '@gooduncles/gu-app-schema';
import { groupBy } from 'lodash';
import {
  IssueCaseAnyCategory,
  ISSUE_TASK_ACTIVE_STATUSES,
  issueCaseCategoryKr,
  issueCaseSubcategoryKr,
  issueTaskCategoryKr,
} from 'src/constants/issue.constants';

import { IssueManageDetailRowData, IssueManageMasterRowData } from 'src/components/Issue/IssueManage/IssueManage.types';

/**
 * IssueCaseCategory / IssueCaseRequestCategory 한글명 반환
 * 매칭되는 한글명이 없으면 원본 값을 그대로 반환
 */
export const getCategoryLabel = (category: string): string => {
  return issueCaseCategoryKr[category as IssueCaseAnyCategory] ?? category;
};

/**
 * IssueCaseSubcategory / IssueCaseRequestSubcategory 한글명 반환
 * 매칭되는 한글명이 없으면 원본 값을 그대로 반환
 */
export const getSubcategoryLabel = (category: string, subcategory: string): string => {
  return issueCaseSubcategoryKr[category as IssueCaseAnyCategory]?.[subcategory] ?? subcategory;
};

/**
 * IssueCase의 카테고리를 한글로 포맷팅
 */
export const formatIssueCaseCategory = (issueCase: IssueCaseDoc): string => {
  const baseCategory = getCategoryLabel(issueCase.category);
  const subcategoryPart = issueCase.subcategory
    ? ` / ${getSubcategoryLabel(issueCase.category, issueCase.subcategory)}`
    : '';
  return baseCategory + subcategoryPart;
};

/**
 * IssueTask의 카테고리를 한글로 포맷팅
 */
export const formatIssueTaskCategory = (task: IssueTaskDoc): string => {
  return issueTaskCategoryKr[task.category as IssueTaskCategory] || task.category;
};

/**
 * IssueCaseDoc, IssueTaskDoc, IssueLiabilityDoc을 IssueManageMasterRowData로 변환
 * @param cases - IssueCaseDoc 배열
 * @param tasks - IssueTaskDoc 배열
 * @param liabilities - IssueLiabilityDoc 배열
 * @returns IssueManageMasterRowData 배열 (status 오름차순, 같은 status면 registeredAt 오름차순 - 오래된 것 먼저)
 */
export const processIssueData = (
  cases: IssueCaseDoc[],
  tasks: IssueTaskDoc[],
  liabilities: IssueLiabilityDoc[] = [],
): IssueManageMasterRowData[] => {
  // 정렬: status 오름차순, 같은 status면 registeredAt 오름차순 (오래된 것이 먼저)
  const sortedCases = [...cases].sort((a, b) => {
    if (a.status !== b.status) {
      return a.status - b.status;
    }
    return a.registeredAt.localeCompare(b.registeredAt);
  });

  // Filter out canceled and deleted tasks, then map by caseId
  const activeTasks = tasks.filter((task) => ISSUE_TASK_ACTIVE_STATUSES.includes(task.status));
  const tasksByCaseId = groupBy(activeTasks, 'issueCaseId');

  // Map liabilities by caseId
  const liabilitiesByCaseId = groupBy(liabilities, 'issueCaseId');

  // Create Case Rows
  return sortedCases.map((issueCase) => {
    const caseTasks = tasksByCaseId[issueCase._id] || [];
    const caseLiabilities = liabilitiesByCaseId[issueCase._id] || [];

    const issueTasks: IssueManageDetailRowData[] = caseTasks.map((task) => ({
      ...task,
      displayCategory: formatIssueTaskCategory(task),
    }));

    return {
      ...issueCase,
      displayCategory: formatIssueCaseCategory(issueCase),
      issueTasks,
      issueLiabilities: caseLiabilities,
    };
  });
};
